/**
 * Create a small keyed request cache that coalesces concurrent calls and
 * optionally reuses successful results for a short ttl.
 *
 * @param {{ ttlMs?: number }} [options]
 */
export function createRequestCoalescer(options = {}) {
  const ttlMs = Math.max(0, options.ttlMs ?? 0);
  const inflight = new Map();
  const cached = new Map();

  function getCachedValue(key) {
    const entry = cached.get(key);
    if (!entry) {
      return undefined;
    }

    if (entry.expiresAt <= Date.now()) {
      cached.delete(key);
      return undefined;
    }

    return entry.value;
  }

  /**
   * @template T
   * @param {string} key
   * @param {() => Promise<T>} loader
   * @returns {Promise<T>} The shared in-flight or cached loader result for this key.
   */
  async function run(key, loader) {
    const cachedValue = getCachedValue(key);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    const existing = inflight.get(key);
    if (existing) {
      return existing;
    }

    const promise = Promise.resolve()
      .then(loader)
      .then((value) => {
        if (ttlMs > 0) {
          cached.set(key, {
            value,
            expiresAt: Date.now() + ttlMs,
          });
        }
        return value;
      })
      .finally(() => {
        inflight.delete(key);
      });

    inflight.set(key, promise);
    return promise;
  }

  function clear(key) {
    if (typeof key === 'string') {
      inflight.delete(key);
      cached.delete(key);
      return;
    }

    inflight.clear();
    cached.clear();
  }

  return {
    run,
    clear,
  };
}
